import constants from '@/lib/constants'
import { getDefaultSheetSelectionItems } from '@/lib/packageBuilder'

import { CadyMarket } from '@/types/CadyMarket'

import { PromptType } from '@VelocitySquared/sunny16-commerce-dataobjects/enums'
import {
  BundleStorefrontDto,
  CartItemCustomizationDetailsDto,
  CartItemUpsertStorefrontDto,
  ImagePromptResponseDto,
  MediaReferenceDto
} from '@VelocitySquared/sunny16-commerce-dataobjects/storefront'

export class CartBundleConfigurator {
  selectedCartBundleItems: CartItemUpsertStorefrontDto[] | null = null
  bundle: BundleStorefrontDto
  market: CadyMarket

  constructor(bundle: BundleStorefrontDto, market: CadyMarket) {
    this.bundle = bundle
    this.market = market
  }

  // define configureSelectedItems function
  configureSelectedItems(): CartItemUpsertStorefrontDto[] | null {
    this.selectedCartBundleItems = this.configureRequiredItemGroups()

    if (this.market === CadyMarket.Underclass) {
      this.selectedCartBundleItems = this.underclassPopulateImagePromptResponses()
    }

    return this.selectedCartBundleItems
  }

  private configureRequiredItemGroups(): CartItemUpsertStorefrontDto[] | null {
    let result: CartItemUpsertStorefrontDto[] | null = null

    this.bundle.itemGroups.forEach((ig) => {
      if (ig.minRequiredItems > 0) {
        const itemsRequired = ig.minRequiredItems
        // Sort a copy: `Array.sort` mutates, and `ig.itemGroupItems` is the catalog store's own array.
        const sortedItems = [...(ig.itemGroupItems ?? [])]
          .sort((a, b) => (a.zOrder || 0) - (b.zOrder || 0))
          .slice(0, itemsRequired)

        sortedItems.forEach((igi) => {
          const cartItem = new CartItemUpsertStorefrontDto()
          cartItem.itemId = igi.itemId
          cartItem.itemGroupItemId = igi.itemGroupItemId
          if (result === null) {
            result = []
          }

          result.push(cartItem)
        })
      }
    })

    // A SheetSelection group must total exactly `maxAllowedItems` for Sunny16 to accept the add,
    // and the loop above leaves it empty (its `minRequiredItems` is 0). Seed a valid default so a
    // freshly-added Package Builder bundle can enter the cart; the customer refines it on the
    // Package Builder step (which overwrites via `upsertCartBundle`). Flag-gated inside the helper.
    const defaultSheetItems = getDefaultSheetSelectionItems(this.bundle)
    if (defaultSheetItems.length > 0) {
      result = [...(result ?? []), ...defaultSheetItems]
    }

    return result
  }

  // This function populates cart items in a cart bundle to be added based on the source catalog bundle
  // Specifically, it sets the prompt responses for image prompts to the default value that Sunny16 expects
  private underclassPopulateImagePromptResponses(): CartItemUpsertStorefrontDto[] {
    // Start with existing selected items or empty array
    const result: CartItemUpsertStorefrontDto[] = this.selectedCartBundleItems
      ? [...this.selectedCartBundleItems]
      : []

    // loop through each item group in the catalog bundle
    this.bundle.itemGroups.forEach((ig) => {
      // check if the item group is included
      if (ig && ig.isIncluded) {
        // loop through each item in the item group
        ig?.itemGroupItems?.forEach((igi) => {
          const promptResponses: ImagePromptResponseDto[] = []

          // loop through each prompt in the item
          igi.item?.prompts?.forEach((prompt) => {
            // check if the prompt is of type ImagePrompt
            if (prompt.promptType === PromptType.ImagePrompt) {
              // create a new PromptResponseDto
              const promptResponse = new ImagePromptResponseDto()

              promptResponse.promptId = prompt.promptId
              // we can hardcode the prompt type since we know it's an ImagePrompt
              promptResponse.promptType = 'ImagePromptResponse'
              promptResponse.promptName = prompt.promptName

              const imagePromptValue = new MediaReferenceDto()

              // set the placeholder canonicalUrl that Sunny16 expects
              imagePromptValue.canonicalUrl = constants.underclassImagePromptPlaceholder

              promptResponse.value = imagePromptValue

              promptResponses.push(promptResponse)
            }
          })

          // Check if item already exists in selectedCartBundleItems
          const existingCartItemIndex = result.findIndex((item) => item.itemId === igi.itemId)

          if (existingCartItemIndex !== -1) {
            // Update existing item
            const existingItem = result[existingCartItemIndex]

            if (!existingItem.cartItemCustomizationDetails) {
              existingItem.cartItemCustomizationDetails = { promptResponses: [] }
            }

            // Merge or replace prompt responses as needed
            existingItem.cartItemCustomizationDetails.promptResponses = promptResponses
          } else {
            // Create new item
            const cartItemCustomizationDetails: CartItemCustomizationDetailsDto = {
              promptResponses: promptResponses
            }

            const cartItem = new CartItemUpsertStorefrontDto()
            cartItem.itemId = igi.itemId
            cartItem.itemGroupItemId = igi.itemGroupItemId
            cartItem.cartItemCustomizationDetails = cartItemCustomizationDetails

            result.push(cartItem)
          }
        })
      }
    })

    return result
  }
}
